// TODO(basket-api): two server-side issues block S3 PUT uploads from the browser:
// 1. The image-uploads S3 bucket needs a CORS rule allowing PUT (and OPTIONS
//    preflight) from each webapp origin; without it the browser blocks the PUT
//    and `fetch` rejects with a TypeError before any HTTP status is observed.
// 2. The presigner currently signs an empty-body CRC32 checksum
//    (`x-amz-checksum-crc32=AAAAAA==`); fix is `applyChecksum: false` (or
//    omit ChecksumAlgorithm) on the AWS SDK v3 getSignedUrl call so non-empty
//    PUT bodies aren't rejected with 403.
export async function uploadFileToPresignedUrl(file: File, uploadUrl: string): Promise<void> {
  let response: Response;
  try {
    response = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
      },
    });
  } catch (e) {
    /* `fetch` only natively rejects with a TypeError on network / CORS-preflight
       failures (no HTTP status is observed). Surface a clearer message; rethrow
       anything else so unexpected errors aren't masked. */
    if (e instanceof TypeError) {
      throw new Error(
        'Photo upload was blocked before reaching storage. Please try again, and if it persists contact support.',
      );
    }
    throw e;
  }

  if (!response.ok) {
    if (response.status === 403) {
      throw new Error(
        'Photo upload was rejected by storage (403). Please try again, and if it persists contact support.',
      );
    }
    throw new Error(`Image upload failed with status ${response.status}`);
  }
}

export interface ImageUploadSlot {
  objectUrl: string;
  uploadUrl: string;
  uploadUrlValidForSeconds: number;
}

/** PUT each file to its presigned slot; returns objectUrls immediately.
 *
 * Upload is best-effort: CORS/network failures are swallowed so the UI can
 * show the blob preview (already cached before this is called) without waiting
 * on S3. When the bucket's CORS rule and presigner checksum are fixed
 * server-side, the PUT will succeed and objectUrl will be a live S3 object.
 */
export async function uploadFilesToPresignedSlots(
  files: File[],
  slots: ImageUploadSlot[],
): Promise<string[]> {
  if (files.length !== slots.length) {
    throw new Error('Number of files must match number of upload slots');
  }

  await Promise.allSettled(
    files.map((file, i) => uploadFileToPresignedUrl(file, slots[i].uploadUrl)),
  );

  return slots.map((s) => s.objectUrl);
}
